import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Stage, Layer, Image, Rect } from 'react-konva';
import { Box, Button, Stack, Typography } from '@mui/joy';
import SendIcon from '@mui/icons-material/Send';
import ClearIcon from '@mui/icons-material/Clear';
import { getDocument } from 'pdfjs-dist';
import { initPdfJsWorker } from '../lib/pdfjs/setupWorker';

initPdfJsWorker();

const ImageRegionSelector = ({
  src,
  documentType = 'image',
  page = 1,
  zoom = 100,
  onCrop,
}) => {
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const [image, setImage] = useState(null);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [rectangle, setRectangle] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [newRectangle, setNewRectangle] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [loading, setLoading] = useState(true);

  // Load image or PDF page
  useEffect(() => {
    if (!src) return;

    setLoading(true);
    setRectangle(null);

    const loadImage = async () => {
      try {
        if (documentType === 'pdf') {
          // Load PDF and render the page as an image
          let loadingTask;

          // For blob URLs or data URLs, pdfjs can handle directly
          if (src.startsWith('blob:') || src.startsWith('data:')) {
            loadingTask = getDocument(src);
          } else {
            // Fetch PDF as blob to avoid CORS issues with S3 presigned URLs
            const response = await fetch(src);
            if (!response.ok) {
              throw new Error(`Failed to fetch PDF: ${response.status}`);
            }
            const arrayBuffer = await response.arrayBuffer();
            loadingTask = getDocument({ data: arrayBuffer });
          }

          const pdf = await loadingTask.promise;
          const pdfPage = await pdf.getPage(page);

          const scale = (zoom / 100) * 2; // Higher resolution
          const viewport = pdfPage.getViewport({ scale });

          const canvas = document.createElement('canvas');
          canvas.height = viewport.height;
          canvas.width = viewport.width;

          const context = canvas.getContext('2d');
          await pdfPage.render({
            canvasContext: context,
            viewport,
          }).promise;

          const img = new window.Image();
          img.src = canvas.toDataURL();
          img.onload = () => {
            setImage(img);
            setLoading(false);
          };
        } else {
          // Load regular image
          const img = new window.Image();
          img.crossOrigin = 'anonymous';
          img.src = src;
          img.onload = () => {
            setImage(img);
            setLoading(false);
          };
          img.onerror = () => {
            console.error('Failed to load image');
            setLoading(false);
          };
        }
      } catch (err) {
        console.error('Error loading document:', err);
        setLoading(false);
      }
    };

    loadImage();
  }, [src, documentType, page, zoom]);

  // Calculate stage size based on container and image
  useEffect(() => {
    if (!containerRef.current || !image) return;

    const updateSize = () => {
      if (!containerRef.current || !image) return;

      const containerWidth = containerRef.current.offsetWidth - 32; // Padding
      const containerHeight = containerRef.current.offsetHeight - 32;

      if (containerWidth <= 0 || containerHeight <= 0) return;

      const imageAspect = image.width / image.height;
      const containerAspect = containerWidth / containerHeight;

      let width, height;
      if (imageAspect > containerAspect) {
        width = Math.min(containerWidth, image.width);
        height = width / imageAspect;
      } else {
        height = Math.min(containerHeight, image.height);
        width = height * imageAspect;
      }

      setStageSize({ width, height });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [image]);

  // Mouse handlers for drawing
  const handleMouseDown = (e) => {
    if (!image || rectangle) return;
    const pos = e.target.getStage().getPointerPosition();
    setIsDrawing(true);
    setNewRectangle({
      x: pos.x,
      y: pos.y,
      width: 0,
      height: 0,
      id: Date.now().toString(),
    });
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    const pos = e.target.getStage().getPointerPosition();
    setNewRectangle((prev) => ({
      ...prev,
      width: pos.x - prev.x,
      height: pos.y - prev.y,
    }));
  };

  const handleMouseUp = () => {
    if (!isDrawing) return;
    setIsDrawing(false);

    // Only set rectangle if it has meaningful size
    if (Math.abs(newRectangle.width) > 10 && Math.abs(newRectangle.height) > 10) {
      // Normalize to positive width/height
      const rect = {
        ...newRectangle,
        x: newRectangle.width < 0 ? newRectangle.x + newRectangle.width : newRectangle.x,
        y: newRectangle.height < 0 ? newRectangle.y + newRectangle.height : newRectangle.y,
        width: Math.abs(newRectangle.width),
        height: Math.abs(newRectangle.height),
      };
      setRectangle(rect);
    }

    setNewRectangle({ x: 0, y: 0, width: 0, height: 0 });
  };

  const handleClear = () => {
    setRectangle(null);
  };

  const handleSend = useCallback(async () => {
    if (!rectangle || !stageRef.current || !image) return;

    // Calculate the crop bounds relative to the original image
    const scaleX = image.width / stageSize.width;
    const scaleY = image.height / stageSize.height;

    const cropBounds = {
      x: Math.round(rectangle.x * scaleX),
      y: Math.round(rectangle.y * scaleY),
      width: Math.round(rectangle.width * scaleX),
      height: Math.round(rectangle.height * scaleY),
    };

    // Create a canvas to crop the region
    const canvas = document.createElement('canvas');
    canvas.width = cropBounds.width;
    canvas.height = cropBounds.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      cropBounds.x,
      cropBounds.y,
      cropBounds.width,
      cropBounds.height,
      0,
      0,
      cropBounds.width,
      cropBounds.height
    );

    const dataUrl = canvas.toDataURL('image/png');

    // Convert to blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();

    if (onCrop) {
      onCrop({
        blob,
        dataUrl,
        bounds: cropBounds,
      });
    }

    setRectangle(null);
  }, [rectangle, image, stageSize, onCrop]);

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
        <Typography level="body-sm">Loading document...</Typography>
      </Stack>
    );
  }

  if (!image) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
        <Typography level="body-sm" color="danger">
          Failed to load document
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 400,
        height: '100%',
        p: 2,
        bgcolor: 'background.level1',
        borderRadius: 'lg',
      }}
    >
      {/* Action buttons when region is selected */}
      {rectangle && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            mb: 2,
            justifyContent: 'center',
          }}
        >
          <Button
            size="sm"
            variant="outlined"
            color="neutral"
            startDecorator={<ClearIcon sx={{ fontSize: 16 }} />}
            onClick={handleClear}
          >
            Clear Selection
          </Button>
          <Button
            size="sm"
            variant="solid"
            color="primary"
            startDecorator={<SendIcon sx={{ fontSize: 16 }} />}
            onClick={handleSend}
          >
            Send to Chat
          </Button>
        </Stack>
      )}

      {/* Canvas Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: rectangle ? 'default' : 'crosshair',
        }}
      >
        <Box
          sx={{
            boxShadow: 'lg',
            bgcolor: 'background.surface',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 'md',
          }}
        >
          <Stage
            ref={stageRef}
            width={stageSize.width}
            height={stageSize.height}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <Layer>
              {image && (
                <Image
                  image={image}
                  width={stageSize.width}
                  height={stageSize.height}
                />
              )}

              {/* Current rectangle being drawn */}
              {isDrawing && (
                <Rect
                  x={newRectangle.x}
                  y={newRectangle.y}
                  width={newRectangle.width}
                  height={newRectangle.height}
                  fill="rgba(25, 118, 210, 0.2)"
                  stroke="#1976d2"
                  strokeWidth={2}
                  dash={[5, 5]}
                />
              )}

              {/* Finalized rectangle */}
              {rectangle && (
                <Rect
                  x={rectangle.x}
                  y={rectangle.y}
                  width={rectangle.width}
                  height={rectangle.height}
                  fill="rgba(25, 118, 210, 0.15)"
                  stroke="#1976d2"
                  strokeWidth={2}
                />
              )}
            </Layer>
          </Stage>
        </Box>
      </Box>
    </Box>
  );
};

export default ImageRegionSelector;
